import { useState, useMemo, useEffect, useRef } from 'react'
import {
  FolderPlus, Building2, BookOpen,
  Folder, Clock, ChevronRight, Search,
  Settings, Loader2,
  LayoutGrid, List, Pencil, Trash2,
  ChevronDown, AlertCircle, User, LogOut, TrendingUp, Layers, Briefcase,
  Sparkles, Wrench, HelpCircle, HardDrive, FileText, Activity, Database, Binary, Brain, GitBranchPlus,
  FileBarChart
} from 'lucide-react'
import NewProjectModal from './NewProjectModal'
import AISetupModal from './AISetupModal'
import ManageAccountsModal from './ManageAccountsModal'
import TargetProfilesModal from './TargetProfilesModal'
import DocsView from '../docs/DocsView'
import AIAssistant from '../designer/AIAssistant'
import { useApplications, useAccounts } from '../../hooks'
import { isQA } from '../../environment'
import './LandingView.css'

// Helper to format relative time
function formatRelativeTime(date) {
  const now = new Date()
  const then = new Date(date)
  const diffMs = now - then
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minutes ago`
  if (diffHours < 24) return `${diffHours} hours ago`
  if (diffDays < 7) return `${diffDays} days ago`
  return `${Math.floor(diffDays / 7)} weeks ago`
}


function LandingView({ onProjectSelect, onGuidedStart, onRunsClick, onOpenReports, onModelTraining, onSchemaTools, onEtlTools, onJobMonitor, onOpenSettings, onOpenHelp, projectError, onClearProjectError }) {
  const [showNewProject, setShowNewProject] = useState(false)
  const [editingProject, setEditingProject] = useState(null)
  const [deleteConfirmProject, setDeleteConfirmProject] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const [showManageAccounts, setShowManageAccounts] = useState(false)
  const [showTargetProfiles, setShowTargetProfiles] = useState(false)
  const [showDocs, setShowDocs] = useState(false)
  const [showAISetup, setShowAISetup] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [selectedAccountId, setSelectedAccountId] = useState(null)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showToolsMenu, setShowToolsMenu] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const userMenuRef = useRef(null)
  const toolsMenuRef = useRef(null)

  // Get accounts and applications from hooks
  const { accounts, loading: accountsLoading } = useAccounts()
  const { applications, loading: appsLoading, createApplication, selectApplication, updateApplication, deleteApplication } = useApplications()

  const loading = accountsLoading || appsLoading

  // Load current user from localStorage
  useEffect(() => {
    const userStr = localStorage.getItem('modernizeit_user')
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr))
      } catch (e) {
        console.error('Failed to parse user data:', e)
      }
    }
  }, [])

  // Handle outside clicks for user menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle outside clicks for tools menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target)) {
        setShowToolsMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Set default account when accounts load
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      const defaultAccount = accounts.find(a => a.isDefault) || accounts[0]
      setSelectedAccountId(defaultAccount.scoutAccountId)
    }
  }, [accounts, selectedAccountId])

  // Get selected account object
  const selectedAccount = useMemo(() => {
    return accounts.find(a => a.scoutAccountId === selectedAccountId)
  }, [accounts, selectedAccountId])

  // Map applications to project format, filtered by selected account
  const recentProjects = useMemo(() => {
    return (applications || [])
      .filter(app => !selectedAccountId || app.scoutAccountId === selectedAccountId)
      .map(app => ({
        id: app.id,
        account: app.scoutAccountId,
        accountName: app.accountName || 'Default Account',
        name: app.name,
        lastOpened: app.updatedAt ? formatRelativeTime(app.updatedAt) : 'Recently',
        _original: app,
      }))
  }, [applications, selectedAccountId])

  const filteredProjects = recentProjects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.account.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.accountName.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleProjectClick = (project) => {
    // Clear any previous error when clicking a project
    if (onClearProjectError) onClearProjectError()
    // If this is a real application from the hook, select it
    if (project._original) {
      selectApplication(project._original)
    }
    onProjectSelect({
      id: project.id,
      account: project.account,
      accountName: project.accountName,
      name: project.name
    })
  }

  const handleCreateProject = async (projectData) => {
    setShowNewProject(false)
    // Create the application through the hook
    try {
      const app = await createApplication({
        name: projectData.name,
        description: projectData.description,
        scoutAccountId: projectData.account,
        accountName: projectData.accountName,
        sourceLanguage: projectData.sourceLanguage || 'cobol',
        targetLanguage: projectData.targetLanguage || 'java',
      })
      if (app) {
        selectApplication(app)
        // Navigate to the new project
        onProjectSelect({
          id: app.id,
          account: projectData.account,
          accountName: projectData.accountName,
          name: app.name
        })
      }
    } catch (err) {
      console.error('Failed to create application:', err)
      // TODO: Show error toast to user
    }
  }

  const handleEditProject = (project) => {
    setEditingProject(project)
  }

  const handleDeleteClick = (project, e) => {
    e.stopPropagation()
    setDeleteError(null)
    setDeleteConfirmProject(project)
    console.log('[DeleteProject] Confirm modal opened for:', project.name, project.id)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmProject) return
    setDeleteLoading(true)
    setDeleteError(null)
    console.log('[DeleteProject] Delete confirmed for:', deleteConfirmProject.name, deleteConfirmProject.id)
    try {
      await deleteApplication(deleteConfirmProject.id)
      console.log('[DeleteProject] Delete succeeded, card removed from list')
      setDeleteConfirmProject(null)
    } catch (err) {
      console.error('[DeleteProject] Delete failed:', err)
      setDeleteError(err.message || 'Delete failed. Please try again.')
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmProject(null)
    setDeleteError(null)
    setDeleteLoading(false)
  }

  const handleUpdateProject = async (projectData) => {
    setEditingProject(null)
    // Update the application through the hook
    try {
      const updatedApp = await updateApplication?.(projectData.id, {
        name: projectData.name,
        description: projectData.description,
        scoutAccountId: projectData.account,
        accountName: projectData.accountName
      })
      if (updatedApp) {
        console.log('Project updated successfully:', updatedApp)
      }
    } catch (err) {
      console.error('Failed to update application:', err)
      // TODO: Show error toast to user
    }
  }

  const handleManageAccounts = () => {
    setShowManageAccounts(true)
  }

  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem('modernizeit_token')
    localStorage.removeItem('modernizeit_user')
    // Reload page to trigger login redirect
    window.location.reload()
  }

  return (
    <div className="landing-view">
      <div className="landing-content">
        {/* Left Side - Actions */}
        <div className="landing-sidebar">
          {/* Account Switcher */}
          <div className="account-switcher">
            <label className="switcher-label">Account</label>
            <div className="switcher-select-wrapper">
              <select
                className="switcher-select"
                value={selectedAccountId || ''}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                disabled={accountsLoading || accounts.length === 0}
              >
                {accounts.map(acc => (
                  <option key={acc.scoutAccountId} value={acc.scoutAccountId}>
                    {acc.name}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="switcher-icon" />
            </div>
          </div>

          <div className="welcome-section">
            <h1>Welcome to RecodeIT</h1>
            <p className="subtitle">Language-Agnostic Code Transformation Platform</p>
          </div>

          {/* Validation Warnings */}
          {accounts.length === 0 && (
            <div className="warning-banner">
              <AlertCircle size={20} />
              <div>
                <strong>No accounts configured</strong>
                <p>Create an account to get started with your first project.</p>
              </div>
            </div>
          )}
          {accounts.length > 0 && applications.length === 0 && (
            <div className="info-banner">
              <AlertCircle size={20} />
              <div>
                <strong>No projects yet</strong>
                <p>Create your first project to start transforming code.</p>
              </div>
            </div>
          )}

          <div className="action-buttons">
            <button
              className="action-btn ai-setup"
              onClick={() => setShowAISetup(true)}
              title="Open HITL Pipeline with AI"
            >
              <Sparkles size={20} />
              <span>Get Started with AI</span>
            </button>

            {/* MANAGE ACCOUNTS — hidden from sidebar; now in top-right User dropdown. Restore by uncommenting.
            <button className="action-btn" onClick={handleManageAccounts}>
              <Building2 size={20} />
              <span>Manage Accounts</span>
            </button>
            */}

            {/* TARGET PROFILES — temporarily hidden, restore when ready */}
            {/* <button className="action-btn" disabled>
              <Layers size={20} />
              <span>fTarget Profiles</span>
            </button> */}

            <button className="action-btn primary" onClick={() => setShowNewProject(true)} title="Create a new workflow project">
              <FolderPlus size={20} />
              <span>Projects</span>
            </button>

            {/* Portfolio — hidden for now (not used yet) */}
            {false && (
            <button className="action-btn" disabled title="Coming soon">
              <Briefcase size={20} />
              <span>Portfolio</span>
            </button>
            )}

            <button className="action-btn" onClick={() => onRunsClick?.(selectedAccountId)} title="View pipeline execution history">
              <TrendingUp size={20} />
              <span>Runs</span>
            </button>

            <button className="action-btn" onClick={() => onOpenReports?.()} title="View analysis reports and dashboards">
              <FileBarChart size={20} />
              <span>Reports</span>
            </button>

            {/* Model Training — hidden for now (not used yet) */}
            {false && (
            <button className="action-btn" disabled>
              <Brain size={20} />
              <span>Model Training</span>
            </button>
            )}

            <button className="action-btn accent" onClick={() => setShowDocs(true)} title="Browse guides and reference documentation">
              <BookOpen size={20} />
              <span>View Documentation</span>
            </button>
          </div>

          <div className="sidebar-footer">
            {/* SETTINGS LINK — hidden; Settings now lives in the top-right header. Restore by uncommenting.
            <button className="settings-link" onClick={onOpenSettings}>
              <Settings size={16} />
              <span>Settings</span>
            </button>
            */}
          </div>
        </div>

        {/* Right Side - Recent Projects */}
        <div className="landing-main">
          <div className="projects-header">
            <div className="projects-title">
              <h2>Projects {loading && <Loader2 size={16} className="spin" />}</h2>
              {selectedAccount && (
                <span className="projects-account">{selectedAccount.name}</span>
              )}
            </div>
            <div className="header-controls">
              <div className="view-toggle">
                <button
                  className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid view"
                >
                  <LayoutGrid size={18} />
                </button>
                <button
                  className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title="List view"
                >
                  <List size={18} />
                </button>
              </div>
              <div className="search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Tools menu — hidden for now (customer questions, needs revisit) */}
              {false && (
              <div className="tools-menu-container" ref={toolsMenuRef}>
                <button
                  className="icon-btn"
                  onClick={() => setShowToolsMenu(!showToolsMenu)}
                  title="Tools"
                >
                  <Wrench size={18} />
                </button>

                {showToolsMenu && (
                  <div className="tools-dropdown">
                    <div className="dropdown-section-label">UTILITIES</div>
                    <button className="dropdown-item" disabled title="Coming soon">
                      <HardDrive size={16} />
                      <span>S3 Browser</span>
                    </button>
                    <button className="dropdown-item" disabled>
                      <FileText size={16} />
                      <span>System Logs</span>
                    </button>
                    <div className="dropdown-divider" />
                    <div className="dropdown-section-label">JOBS</div>
                    <button className="dropdown-item" onClick={() => { setShowToolsMenu(false); onJobMonitor?.() }}>
                      <Activity size={16} />
                      <span>Job Monitor</span>
                    </button>
                    <div className="dropdown-divider" />
                    <div className="dropdown-section-label">AI</div>
                    <button className="dropdown-item" disabled>
                      <Sparkles size={16} />
                      <span>Agent Studio</span>
                    </button>
                    <div className="dropdown-divider" />
                    <div className="dropdown-section-label">DATA</div>
                    <button className="dropdown-item" onClick={() => { setShowToolsMenu(false); onEtlTools?.() }}>
                      <GitBranchPlus size={16} />
                      <span>ETL Pipeline Builder</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowToolsMenu(false); onSchemaTools?.() }}>
                      <Database size={16} />
                      <span>Schema Tool</span>
                    </button>
                    <div className="dropdown-divider" />
                    <div className="dropdown-section-label">CODE</div>
                    <button className="dropdown-item" disabled title="Open from a workflow node gate check">
                      <Binary size={16} />
                      <span>Code Resolution Workbench</span>
                    </button>
                    <div className="dropdown-divider" />
                    <div className="dropdown-section-label">DASHBOARD</div>
                    <button className="dropdown-item" disabled>
                      <TrendingUp size={16} />
                      <span>Dashboard Editor</span>
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Settings */}
              <button className="icon-btn" onClick={onOpenSettings} title="Settings">
                <Settings size={18} />
              </button>

              {/* Help */}
              <button className="icon-btn" onClick={onOpenHelp} title="Help & Guidance">
                <HelpCircle size={18} />
              </button>

              {/* User Menu */}
              {currentUser && (
                <div className="user-menu-container" ref={userMenuRef}>
                  <button
                    className="user-menu-button"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    title={currentUser.email}
                  >
                    <User size={18} />
                    <span className="user-name">{currentUser.name || currentUser.email}</span>
                    <ChevronDown size={14} className={`chevron ${showUserMenu ? 'rotated' : ''}`} />
                  </button>

                  {showUserMenu && (
                    <div className="user-dropdown">
                      <div className="user-dropdown-header">
                        <div className="user-dropdown-name">{currentUser.name}</div>
                        <div className="user-dropdown-email">{currentUser.email}</div>
                        {currentUser.account_name && (
                          <div className="user-dropdown-account">{currentUser.account_name}</div>
                        )}
                      </div>
                      <div className="user-dropdown-divider"></div>
                      <button
                        className="user-dropdown-item"
                        onClick={() => { setShowUserMenu(false); handleManageAccounts() }}
                      >
                        <Building2 size={16} />
                        <span>Manage Accounts</span>
                      </button>
                      <div className="user-dropdown-divider"></div>
                      <button
                        className="user-dropdown-item logout"
                        onClick={handleLogout}
                      >
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="projects-grid">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className={`project-card${projectError === project.name ? ' project-card--error' : ''}`}
                  onClick={() => handleProjectClick(project)}
                >
                  <div className="card-header">
                    <h3 className="card-title">{project.name}</h3>
                    <div className="card-actions">
                      <button
                        className="card-action-btn"
                        title="Edit"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditProject(project)
                        }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="card-action-btn card-action-btn--delete"
                        title="Delete project"
                        onClick={(e) => handleDeleteClick(project, e)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="card-id">app_{project.name}_{project.id}</div>
                  <div className="card-details">
                    <div className="card-row">
                      <span className="card-label">Description:</span>
                      <span className="card-value">{project.accountName || '-'}</span>
                    </div>
                    <div className="card-row">
                      <span className="card-label">Files:</span>
                      <span className="card-value">{project._original?.fileCount || '-'}</span>
                    </div>
                    <div className="card-row">
                      <span className="card-label">Created:</span>
                      <span className="card-value">{project.lastOpened}</span>
                    </div>
                  </div>
                  {projectError === project.name ? (
                    <div className="card-error-badge">
                      <AlertCircle size={13} />
                      <span>No analysis available</span>
                    </div>
                  ) : (
                    <div className="card-tags">
                      <span className="tag tag-cobol">COBOL</span>
                      <span className="tag tag-java">JAVA</span>
                    </div>
                  )}
                </div>
              ))}

              {filteredProjects.length === 0 && !loading && (
                <div className="no-projects">
                  {searchTerm ? (
                    <p>No projects found matching "{searchTerm}"</p>
                  ) : (
                    <>
                      <p>No projects yet</p>
                      <p className="no-projects-hint">Click "New Project" to create your first project</p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="projects-list">
              {filteredProjects.map((project) => (
                <button
                  key={project.id}
                  className={`project-item${projectError === project.name ? ' project-item--error' : ''}`}
                  onClick={() => handleProjectClick(project)}
                >
                  <Folder size={20} className="project-icon" />
                  <div className="project-info">
                    <span className="project-name">
                      <span className="app-name">{project.name}</span>
                    </span>
                    <span className="project-meta">
                      <span className="account-name">{project.accountName}</span>
                      <span className="dot">·</span>
                      <Clock size={12} />
                      <span>{project.lastOpened}</span>
                      {projectError === project.name && (
                        <>
                          <span className="dot">·</span>
                          <AlertCircle size={12} className="list-error-icon" />
                          <span className="list-error-text">No analysis available</span>
                        </>
                      )}
                    </span>
                  </div>
                  <ChevronRight size={18} className="project-arrow" />
                </button>
              ))}

              {filteredProjects.length === 0 && !loading && (
                <div className="no-projects">
                  {searchTerm ? (
                    <p>No projects found matching "{searchTerm}"</p>
                  ) : (
                    <>
                      <p>No projects yet</p>
                      <p className="no-projects-hint">Click "New Project" to create your first project</p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <button className="browse-all-btn">
            <span>Browse All Projects</span>
          </button>
        </div>
      </div>

      {/* Bottom Log Panel */}
      <div className="landing-logs">
        <div className="logs-header">
          <span>LOGS</span>
        </div>
        <div className="logs-content">
          <div className="log-entry">
            <span className="log-time">[{new Date().toLocaleString()}]</span>
            <span className="log-level info">[INFO]</span>
            <span className="log-message">RecodeIT started successfully</span>
          </div>
        </div>
      </div>

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      {deleteConfirmProject && (
        <div className="delete-modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <Trash2 size={18} className="delete-modal-icon" />
              <span>Delete Project</span>
            </div>
            <div className="delete-modal-body">
              <p className="delete-modal-name">"{deleteConfirmProject.name}"</p>
              <p className="delete-modal-warning">
                This will permanently delete the project and its run history from the database.
                This action cannot be undone.
              </p>
              {deleteError && (
                <div className="delete-modal-error">
                  <AlertCircle size={14} />
                  <span>{deleteError}</span>
                </div>
              )}
            </div>
            <div className="delete-modal-footer">
              <button
                className="delete-modal-btn delete-modal-btn--cancel"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="delete-modal-btn delete-modal-btn--confirm"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <Loader2 size={14} className="spin" />
                    Deleting…
                  </>
                ) : (
                  'Delete Project'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New/Edit Project Modal */}
      <NewProjectModal
        isOpen={showNewProject || !!editingProject}
        onClose={() => {
          setShowNewProject(false)
          setEditingProject(null)
        }}
        onCreate={handleCreateProject}
        onUpdate={handleUpdateProject}
        editProject={editingProject}
        defaultAccount={selectedAccountId}
        onManageAccounts={handleManageAccounts}
      />

      {/* Manage Accounts Modal */}
      <ManageAccountsModal
        isOpen={showManageAccounts}
        onClose={() => setShowManageAccounts(false)}
        onOpenSettings={onOpenSettings}
      />

      {/* Target Profiles Modal */}
      <TargetProfilesModal
        isOpen={showTargetProfiles}
        onClose={() => setShowTargetProfiles(false)}
        accountId={selectedAccountId}
      />

      {/* AI Setup Modal */}
      <AISetupModal
        isOpen={showAISetup}
        onClose={() => setShowAISetup(false)}
        selectedAccountId={selectedAccountId}
        onSuccess={(createdProject) => {
          if (createdProject) {
            console.log('[LandingView] AI Setup complete, routing to guided upload:', createdProject.name)
            selectApplication(createdProject)
            const projectData = {
              id: createdProject.id,
              account: createdProject.scoutAccountId || createdProject.account,
              accountName: createdProject.accountName || '',
              name: createdProject.name
            }
            // Route to guided upload flow if available, otherwise normal project select
            if (onGuidedStart) {
              onGuidedStart(projectData)
            } else {
              onProjectSelect(projectData)
            }
          }
        }}
      />

      {/* Documentation View */}
      <DocsView
        isOpen={showDocs}
        onClose={() => setShowDocs(false)}
      />

      {/* Workspace Assistant */}
      <AIAssistant
        account={selectedAccountId}
        application={null}
        pageType="projects"
        pageContext={{
          account_name: selectedAccount?.name || null,
          project_count: filteredProjects.length,
        }}
      />
    </div>
  )
}

export default LandingView
